import { m } from "#/paraglide/messages";
import { Button, cn, Dialog, DialogContent, DialogHeader, DialogTitle } from "@beweegbaas/ui";
import { createFileRoute, Outlet, useMatch } from "@tanstack/react-router";
import { useState } from "react";
import { HistoryBackLink } from "#/components/back-link";
import { containerStyles, Page } from "#/components/page";
import { TabsNavigation } from "#/components/tabs-navigation";
import { AddNotitieDialog } from "#/modules/school.students/components/add-notitie-dialog";
import { AvatarPickerDialog } from "#/modules/school.students/components/avatar-picker-dialog";
import { EditStudentForm } from "#/modules/school.students/components/edit-student-form";
import { getStudentDetailData } from "#/modules/school.students/students.functions";
import { getLeerjaarTranslation } from "#/modules/util.i18n/enum-mapping";

// Student detail tab layout (portal's `_protected.$school.students.$id`). Loads the student +
// klassen for the header/edit modal, and renders the tab bar with the active tab's Outlet.
export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/students/$id")(
  {
    loader: ({ params }) => getStudentDetailData({ data: { leerlingId: params.id } }),
    component: StudentDetailLayout,
  },
);

function StudentDetailLayout() {
  const { locale, schoolyear, school, id } = Route.useParams();
  const { student, avatarNummer, klassen } = Route.useLoaderData();
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const notitiesTab = useMatch({
    from: "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/notities",
    shouldThrow: false,
  });

  return (
    <Page>
      <Page.Header>
        <HistoryBackLink
          fallback={{ from: Route.fullPath, to: ".." }}
          className="max-md:self-start max-sm:mb-4"
        />

        <div className="flex items-center gap-2 text-sm">
          {avatarNummer != null && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setAvatarDialogOpen(true)}
              className="size-10 shrink-0 rounded-lg p-0"
              aria-label="Avatar wijzigen"
            >
              <img
                alt=""
                src={`/avatars/animals-5/${avatarNummer}.svg`}
                className="size-8 rounded-lg object-contain"
              />
            </Button>
          )}
          <span>
            {student?.firstName} {student?.lastName}
          </span>
          <span className="opacity-20">/</span>
          <div className="text-muted-foreground">
            {student?.klasName}{" "}
            {student?.leerjaar ? getLeerjaarTranslation(student.leerjaar) : null}
          </div>
          <Button
            type="button"
            variant="flat"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setEditDialogOpen(true)}
            aria-label={m["student.bewerk"]()}
          >
            <IconMaterialSymbolsEdit className="size-4" />
          </Button>
        </div>
        {notitiesTab && (
          <Page.Header.Actions>
            <Page.Header.MainAction>
              <AddNotitieDialog leerlingId={id} />
            </Page.Header.MainAction>
          </Page.Header.Actions>
        )}
      </Page.Header>
      <Page.Subnavigation>
        <TabsNavigation className="min-w-0 grow">
          <TabsNavigation.List>
            <TabsNavigation.Link
              to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
              params={{ locale, schoolyear, school, id }}
            >
              Overzicht
            </TabsNavigation.Link>
            <TabsNavigation.Link
              to="/$locale/{-$schoolyear}/$school/students/$id/results"
              params={{ locale, schoolyear, school, id }}
            >
              Beweegresultaten
            </TabsNavigation.Link>
            <TabsNavigation.Link
              to="/$locale/{-$schoolyear}/$school/students/$id/social-results"
              params={{ locale, schoolyear, school, id }}
            >
              Sociaal-emotionele resultaten
            </TabsNavigation.Link>
            <TabsNavigation.Link
              to="/$locale/{-$schoolyear}/$school/students/$id/statistics"
              params={{ locale, schoolyear, school, id }}
            >
              Statistieken
            </TabsNavigation.Link>
            <TabsNavigation.Link
              to="/$locale/{-$schoolyear}/$school/students/$id/notities"
              params={{ locale, schoolyear, school, id }}
            >
              Notities
            </TabsNavigation.Link>
            <TabsNavigation.Link
              to="/$locale/{-$schoolyear}/$school/students/$id/sport-participation"
              params={{ locale, schoolyear, school, id }}
            >
              {m["sportdeelname.title"]()}
            </TabsNavigation.Link>
          </TabsNavigation.List>
        </TabsNavigation>
      </Page.Subnavigation>
      <Page.Body className={cn(containerStyles({ container: "default" }), "pb-10")}>
        <Outlet />
      </Page.Body>
      <AvatarPickerDialog
        open={avatarDialogOpen}
        onOpenChange={setAvatarDialogOpen}
        currentAvatarNummer={avatarNummer}
        studentId={student?.id}
        studentName={student ? `${student.firstName} ${student.lastName}` : undefined}
        klasId={student?.klasId}
      />

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="md:max-w-lg!" showCloseButton>
          <DialogHeader>
            <DialogTitle>{m["student.bewerk"]()}</DialogTitle>
          </DialogHeader>
          <EditStudentForm
            student={student ?? null}
            klassen={klassen}
            onSuccess={() => setEditDialogOpen(false)}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Page>
  );
}
